import clsx from "clsx";
import React from "react";
import { LayoutHeight, LayoutPaddings, LayoutWidth, MediaSpecific, heightClass, mediaSpecificClassSWL, paddingsClassMSL, widthClass } from "../Layout";
import { MotionProps, motionAttrs } from "../Motion";
import { Color, ColorType, colorProps } from "../Typography";
import css from "./card.module.scss";
import { Patent } from '@/components';
import Link from "next/link";

export const enum CardVariant
{
    Column,
    ColumnReversed,
    ColumnSpaceBetween,
    ColumnReversedSpaceBetween,
    Row,
    RowReversed,
    RowSpaceBetween,
    RowReversedSpaceBetween,
    CenteredContent,
    CenteredImage,
}

export interface CardLayout extends LayoutHeight, LayoutPaddings, LayoutWidth
{
    variant?: CardVariant;
}

export interface CardProps extends React.ComponentProps<"div">
{
    bgColor?: Color | string;
    layout?: MediaSpecific<CardLayout>;
    motion?: MotionProps;
    textColor?: Color | string;
    useWrapper?: boolean;
    hiddenOnMobile?: boolean;
    patent?: string;
    link?: string;
}

export const Card = ({ bgColor = Color.MutedTan, className, hiddenOnMobile = false, layout, motion, style, textColor = Color.WarmGray, patent, useWrapper, link, ...props }: CardProps): React.ReactComponentElement<"div", CardProps> => {

    const {className: bgColorClass, style: bgColorStyle} = colorProps(bgColor, ColorType.Background);
    const {className: textColorClass, style: textColorStyle} = colorProps(textColor, ColorType.Text);

    const cardClasses = clsx(
        css["card"],
        useWrapper ? undefined : widthClass(layout),
        paddingsClassMSL(layout),
        heightClass(layout),
        bgColorClass,
        textColorClass,
        mediaSpecificClassSWL("column", layout, "variant", CardVariant.Column, c => css[c]),
        mediaSpecificClassSWL("column-reversed", layout, "variant", CardVariant.ColumnReversed, c => css[c]),
        mediaSpecificClassSWL("column-space-between", layout, "variant", CardVariant.ColumnSpaceBetween, c => css[c]),
        mediaSpecificClassSWL("column-reversed-space-between", layout, "variant", CardVariant.ColumnReversedSpaceBetween, c => css[c]),
        mediaSpecificClassSWL("row", layout, "variant", CardVariant.Row, c => css[c]),
        mediaSpecificClassSWL("row-reversed", layout, "variant", CardVariant.RowReversed, c => css[c]),
        mediaSpecificClassSWL("row-space-between", layout, "variant", CardVariant.RowSpaceBetween, c => css[c]),
        mediaSpecificClassSWL("row-reversed-space-between", layout, "variant", CardVariant.RowReversedSpaceBetween, c => css[c]),
        mediaSpecificClassSWL("centered-content", layout, "variant", CardVariant.CenteredContent, c => css[c]),
        mediaSpecificClassSWL("centered-image", layout, "variant", CardVariant.CenteredImage, c => css[c]),
        !!hiddenOnMobile && css["display-mobile-only"],
        className,
    );

    const cardStyles = {
        ...bgColorStyle,
        ...textColorStyle,
        ...style
    };

    const Content = (
        <div {...props} className={cardClasses} style={cardStyles} {...motionAttrs(motion)} />
    );

    const WrappedContent = link ? (
        <Link href={link} className={css["link-wrapper"]}>
            {Content}
        </Link>
    ) : (
        Content
    );

    return useWrapper ? (
        <div
            className={clsx(
                css["wrapper"],
                widthClass(layout),
                !!hiddenOnMobile && css["display-mobile-only"]
            )}
            {...motionAttrs(motion)}
        >
            {WrappedContent}
            {patent && <Patent patent={patent}/>}
        </div>
    ) : (
        WrappedContent
    );
}
